import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { formatNumber } from '../admin/format';

export interface BarListItem {
  key: string;
  label: ReactNode;
  value: number;
  /** A small figure or badge after the value: a change, a share. */
  secondary?: ReactNode;
  /** Something before the label: a thumbnail, a flag, an icon. */
  leading?: ReactNode;
  href?: string;
  onClick?: () => void;
  /** Mark one row as the vendor's own (their site among competitors). */
  highlight?: boolean;
}

/**
 * A ranked list whose rows carry their own bar — top products, cities,
 * search terms. Each row's shaded width is its share of the largest, so the
 * order and the gaps read at a glance without a chart.
 */
export function BarList({
  items,
  format = (v) => formatNumber(v),
  max,
  className,
  emptyText,
  testId,
}: {
  items: BarListItem[];
  format?: (value: number) => ReactNode;
  /** The value a full bar stands for; defaults to the largest. */
  max?: number;
  className?: string;
  emptyText?: ReactNode;
  testId?: string;
}) {
  const top = max ?? Math.max(0, ...items.map((i) => i.value));
  if (!items.length) return emptyText ? <p className="py-6 text-center text-sm text-muted-foreground">{emptyText}</p> : null;

  return (
    <ul className={cn('flex flex-col gap-1', className)} data-testid={testId}>
      {items.map((item) => {
        const width = top > 0 ? Math.max(2, (item.value / top) * 100) : 0;
        const row = (
          <div className="relative flex h-9 items-center gap-3 px-2.5">
            <div
              className={cn('absolute inset-y-0 start-0 rounded-md', item.highlight ? 'bg-foreground/15' : 'bg-muted')}
              style={{ width: `${width}%` }}
              aria-hidden
            />
            {item.leading && <span className="relative shrink-0">{item.leading}</span>}
            <span className={cn('relative min-w-0 flex-1 truncate text-sm', item.highlight && 'font-medium')}>{item.label}</span>
            <span className="relative flex shrink-0 items-baseline gap-2 tabular-nums">
              <span className="text-sm font-medium">{format(item.value)}</span>
              {item.secondary && <span className="text-xs text-muted-foreground">{item.secondary}</span>}
            </span>
          </div>
        );
        const interactive = 'block rounded-md hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';
        return (
          <li key={item.key}>
            {item.href ? (
              <Link to={item.href} className={interactive}>
                {row}
              </Link>
            ) : item.onClick ? (
              <button type="button" onClick={item.onClick} className={cn(interactive, 'w-full text-start')}>
                {row}
              </button>
            ) : (
              row
            )}
          </li>
        );
      })}
    </ul>
  );
}
